import { useRef, useMemo, useCallback, useState, useEffect } from 'react';
import { useRequestUiStore } from '../../store/requestUiStore';
import { sendRequest } from '../../utils/tauri';
import { buildScriptVariableMap, withheldSecretKeys, filterScriptEnvMutations } from '../../utils/scriptVariables';
import {
  getScriptEnvOverlayValue,
  scriptEnvOverlaySnapshot,
  applyScriptEnvOverlay,
  replaceScriptEnvOverlaySource,
  subscribeScriptEnvOverlay,
} from '../../utils/scriptEnvOverlay';
import { buildRequestUrl } from '../../utils/urlParams';
import { createScopedVariableResolver } from '../../utils/scopedVariableResolver';
import { useVariablesStore } from '../../store/variablesStore';
import { makeKvRowId, ensureTrailingEmptyKvRow } from '../../utils/kvRows';
import { normalizeGraphQLBody, introspectSchema, shouldAutoIntrospect } from '../../utils/graphqlSchema';
import { lookupScopedVariable } from '../../utils/variablePrecedence';

/** Variable scopes and substitution for the request, script env overlays, body type changes and GraphQL introspection. */
export function useRequestVariablesAndGraphql({
  url,
  body,
  folderVariables,
  selectedEnvironment,
  workspaceVariables,
  globalVariables,
  collectionVariables,
  showToast,
  params,
  headers,
  method,
  setMethod,
  setHeaders,
  setBody,
  bodyType,
  setBodyType,
  rawBodyLanguage,
  gqlAutoFetch,
  setGqlSchemaStatus,
  setGqlSchema,
  setGqlSchemaError,
  gqlFetchNonce,
  setGqlFetchNonce,
  gqlAutoFetchedUrlRef,
  tabFieldsRef,
  substituteVariablesRef,
  notifyChange,
}) {
  // Variable substitution function
  // Local variables set by pm.variables.set() in a pre-request script — highest priority scope,
  // cleared after each send.
  const scriptLocalVarsRef = useRef({});

  // Global + persisted, NOT component-local: this used to be useState(false),
  // so every unmount (any page switch) closed the panel behind the user's back.
  // See store/requestUiStore.js. The toggle itself is RequestVariablesToggle,
  // rendered beside Save in the header row.
  const variablesPanelOpen = useRequestUiStore((s) => s.variablesPanelOpen);
  const setVariablesPanelOpen = useRequestUiStore((s) => s.setVariablesPanelOpen);
  const allEnvironments = useVariablesStore((st) => st.environments);

  /**
   * Where a variable's value comes FROM, not just what it is.
   *
   * The panel shows scope beside each value, because "the URL is wrong" and
   * "the URL is right but resolving from the wrong scope" look identical until
   * somebody says which one won.
   *
   * Same precedence as substituteVariables below; if these two ever disagree
   * the panel becomes a liar, which is worse than not having it.
   */
  // The stored scopes, keyed for utils/variablePrecedence (the ONE order).
  const storedScopes = useMemo(() => ({
    folder: folderVariables,
    collection: collectionVariables,
    environment: selectedEnvironment?.variables,
    workspace: workspaceVariables,
    global: globalVariables,
  }), [folderVariables, collectionVariables, selectedEnvironment, workspaceVariables, globalVariables]);

  const resolveVariableScope = useCallback(
    (name) => {
      if (Object.prototype.hasOwnProperty.call(scriptLocalVarsRef.current, name)) {
        return { scope: 'script', value: scriptLocalVarsRef.current[name] };
      }
      const overlay = getScriptEnvOverlayValue(name);
      if (overlay !== undefined) return { scope: 'session', value: overlay };
      const hit = lookupScopedVariable(name, storedScopes);
      if (!hit) return null;
      const { scope, variable } = hit;
      const out = { scope, value: variable.value, options: variable.options };
      if (scope === 'folder') {
        Object.assign(out, {
          secret: variable.secret === true,
          folderId: variable.folderId,
          folderName: variable.folderName,
          collectionId: variable.collectionId,
        });
      }
      return out;
    },
    [storedScopes]
  );

  // THE resolver every request kind sends with (utils/scopedVariableResolver):
  // script-local (pm.variables.set) -> this session's boo.environment.set()
  // overlay (COL-03) -> stored scopes in THE order -> dynamic `$…` variables.
  const substituteVariables = createScopedVariableResolver(storedScopes, {
    localVars: () => scriptLocalVarsRef.current,
  });

  substituteVariablesRef.current = substituteVariables;

  const buildUrl = () => {
    const substitutedUrl = substituteVariables(url);
    const enabledParams = params
      .filter((p) => p.key && p.enabled)
      .map((p) => ({
        key: substituteVariables(p.key),
        value: substituteVariables(p.value ?? ''),
        enabled: true,
      }));
    return buildRequestUrl(substitutedUrl, enabledParams);
  };

  /**
   * The scopes a script is allowed to see, in one place (COL-02). Identical to
   * what `collectionRunner` assembles, so a script behaves the same on Send as
   * it does under Run collection.
   */
  const buildScriptScopes = useCallback(() => ({
    globalVariables,
    workspaceVariables,
    environmentVariables: selectedEnvironment?.variables,
    collectionVariables,
    folderVariables,
  }), [globalVariables, workspaceVariables, selectedEnvironment, collectionVariables, folderVariables]);

  /** Flat key→value map for sandbox use, minus withheld secrets, plus this session's script writes. */
  const buildEnvVariableMap = useCallback(
    () => buildScriptVariableMap(buildScriptScopes(), scriptEnvOverlaySnapshot()),
    [buildScriptScopes]
  );

  /**
   * COL-03. `boo.environment.set()` used to call
   * `onVariableUpdate(..., { scope: 'environment' })`, writing into the user's
   * real, persisted, team-synced environment — so an imported collection's
   * test script could repoint `{{baseUrl}}` for every other collection in the
   * workspace, and for every teammate. Writes now land in the session overlay
   * (`scriptEnvOverlay`), the same throwaway-copy model `collectionRunner`
   * has always used, widened from "the run" to "the session" so that the
   * workflow the persistence existed for — a login script setting `{{token}}`
   * for the next tab — keeps working. Nothing here reaches disk or sync.
   *
   * `sources` (see overlaySources below) are this request's script phase
   * under each key it has had. `replace`: the phase completed, so what it set
   * last time is replaced by what it set now (replaceScriptEnvOverlaySource) —
   * including nothing, for a script that was deleted (owner bug, 16.10.2026).
   * Without `replace` the writes are only added (a test script that threw).
   */
  const applyScriptEnvMutations = useCallback((envMutations, sources = [], { replace = false, script = '' } = {}) => {
    if (!replace && (!envMutations || Object.keys(envMutations).length === 0)) return;
    const { accepted, refused } = filterScriptEnvMutations(
      envMutations,
      withheldSecretKeys(buildScriptScopes())
    );
    if (refused.length > 0) {
      showToast(
        `Script tried to overwrite secret variable${refused.length > 1 ? 's' : ''}: ${refused.join(', ')}`,
        'error'
      );
    }
    if (replace) replaceScriptEnvOverlaySource(sources, accepted, { script });
    else applyScriptEnvOverlay(accepted, sources[0] ?? null);
  }, [buildScriptScopes, showToast]);

  // The overlay lives outside React, so re-render when it changes to keep the
  // `{{var}}` previews in the URL/header inputs showing what would actually be
  // sent.
  const [, bumpOverlayVersion] = useState(0);
  useEffect(
    () => subscribeScriptEnvOverlay(() => bumpOverlayVersion((n) => n + 1)),
    []
  );

  const resolveGqlEndpointUrl = useCallback(() => {
    const sub = substituteVariablesRef.current;
    if (!sub) return '';
    const { url: rawUrl, params: rawParams } = tabFieldsRef.current;
    if (!rawUrl?.trim()) return '';
    const enabledParams = (rawParams || [])
      .filter((p) => p.key && p.enabled)
      .map((p) => ({
        key: sub(p.key),
        value: sub(p.value ?? ''),
        enabled: true,
      }));
    return buildRequestUrl(sub(rawUrl), enabledParams)?.trim() || '';
  }, [substituteVariablesRef, tabFieldsRef]);

  const handleBodyTypeChange = useCallback((id) => {
    setBodyType(id);
    if (id !== 'graphql') {
      setGqlSchemaStatus('idle');
      setGqlSchemaError(null);
      gqlAutoFetchedUrlRef.current = '';
      setGqlSchema(null);
    }
    const patch = { bodyType: id, rawBodyLanguage };
    if (id === 'graphql') {
      gqlAutoFetchedUrlRef.current = '';
      setGqlSchema(null);
      setGqlSchemaStatus('idle');
      setGqlSchemaError(null);
      setGqlFetchNonce((n) => n + 1);
      if (method !== 'POST') {
        setMethod('POST');
        patch.method = 'POST';
      }
      const hasContentType = headers.some((h) => h.key && h.enabled !== false && h.key.toLowerCase() === 'content-type');
      if (!hasContentType) {
        const newHeaders = ensureTrailingEmptyKvRow(
          [...headers.filter((h) => h.key !== ''), { key: 'Content-Type', value: 'application/json', enabled: true, _id: makeKvRowId('h') }],
          'h'
        );
        setHeaders(newHeaders);
        patch.headers = newHeaders;
      }
      if (typeof body !== 'object' || body === null || Array.isArray(body)) {
        const gqlBody = normalizeGraphQLBody(body);
        setBody(gqlBody);
        patch.body = gqlBody;
      }
    } else if (typeof body === 'object' && body !== null) {
      setBody('');
      patch.body = '';
    }
    notifyChange(patch);
  }, [method, headers, body, rawBodyLanguage, notifyChange, gqlAutoFetchedUrlRef, setBody, setBodyType, setGqlFetchNonce, setGqlSchema, setGqlSchemaError, setGqlSchemaStatus, setHeaders, setMethod]);

  const doGqlIntrospect = useCallback(async () => {
    const resolvedUrl = resolveGqlEndpointUrl();
    if (!resolvedUrl) return;
    setGqlSchemaStatus('loading');
    setGqlSchemaError(null);
    try {
      const sub = substituteVariablesRef.current;
      const headersObj = { 'Content-Type': 'application/json' };
      (tabFieldsRef.current.headers || []).forEach((h) => {
        if (h.key && h.enabled) headersObj[sub(h.key)] = sub(h.value ?? '');
      });
      const { schema, error } = await introspectSchema(sendRequest, {
        url: resolvedUrl,
        headers: headersObj,
      });
      if (schema) {
        setGqlSchema(schema);
        gqlAutoFetchedUrlRef.current = resolvedUrl;
        setGqlSchemaStatus('ok');
      } else {
        setGqlSchemaError(error);
        setGqlSchemaStatus('error');
      }
    } catch (err) {
      setGqlSchemaError(err.message || 'Introspection failed');
      setGqlSchemaStatus('error');
    }
  }, [resolveGqlEndpointUrl, gqlAutoFetchedUrlRef, setGqlSchema, setGqlSchemaError, setGqlSchemaStatus, substituteVariablesRef, tabFieldsRef]);

  useEffect(() => {
    const resolvedUrl = resolveGqlEndpointUrl();
    if (!shouldAutoIntrospect({
      autoFetch: gqlAutoFetch,
      bodyType,
      url,
      resolvedUrl,
      lastFetchedUrl: gqlAutoFetchedUrlRef.current,
    })) return;
    const timer = setTimeout(() => doGqlIntrospect(), 500);
    return () => clearTimeout(timer);
  }, [gqlAutoFetch, url, params, bodyType, gqlFetchNonce, selectedEnvironment, globalVariables, collectionVariables, folderVariables, workspaceVariables, doGqlIntrospect, resolveGqlEndpointUrl, gqlAutoFetchedUrlRef]);

  useEffect(() => {
    if (!gqlAutoFetch) {
      setGqlSchemaStatus('idle');
      setGqlSchemaError(null);
      gqlAutoFetchedUrlRef.current = '';
      setGqlSchema(null);
    }
  }, [gqlAutoFetch, gqlAutoFetchedUrlRef, setGqlSchema, setGqlSchemaError, setGqlSchemaStatus]);

  return {
    scriptLocalVarsRef,
    variablesPanelOpen,
    setVariablesPanelOpen,
    allEnvironments,
    storedScopes,
    resolveVariableScope,
    substituteVariables,
    buildUrl,
    buildScriptScopes,
    buildEnvVariableMap,
    applyScriptEnvMutations,
    handleBodyTypeChange,
    doGqlIntrospect,
  };
}
